/**
 * What one run cost, and what could not be costed.
 *
 * One component serves the sandbox result panel and the task detail screen,
 * as ProfilePanel does. The total never stands alone: it carries its
 * reported and rated shares, and a line that was not costed says why in
 * place of an amount. An event with a vendor's receipt is one reported line,
 * converted at the exchange rate of its day and frozen there; its units are
 * shown for information and carry no amount. An event without one is a line
 * per unit, rated from the card. An event whose costed lines sum below the
 * ฿0.01 floor carries one more line, the minimum charge, with the difference.
 */
import { ApiError } from '@optum/api-client/client';
import { exportTaskCostEvents, getTaskCost } from '@optum/api-client/endpoints/cost';
import type { CostLine, TaskCost } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDate, perMillion, quantity, thb, thbExact } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

import { DataTable } from '../../ui/DataTable.js';
import { StatRow } from '../../ui/StatRow.js';
import { Badge, type BadgeTone } from '../../ui/finixt.js';
import { ExportMenu } from './ExportMenu.js';
import { sharePercent } from './GapBanner.js';

const STATUS_LABEL: Record<CostLine['status'], { label: string; color: BadgeTone }> = {
  priced: { label: 'rated', color: 'ok' },
  reported: { label: 'reported', color: 'info' },
  included: { label: 'included', color: 'neutral' },
  unpriced: { label: 'unpriced', color: 'neutral' },
  no_rate: { label: 'no rate', color: 'warn' },
  no_fx: { label: 'no exchange rate', color: 'warn' },
};

/** "input_tokens 6,306 · output_tokens 498": an event's units as one line. */
function unitsText(units: Record<string, number>): string {
  return Object.entries(units)
    .map(([unit, value]) => `${unit} ${quantity(value)}`)
    .join(' · ');
}

/** Fetch one task's cost; a 404 means the run recorded no usage, which is `null` here. */
export function useTaskCost(taskId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.taskCost(taskId),
    queryFn: async (): Promise<TaskCost | null> => {
      try {
        return await getTaskCost(taskId);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled,
  });
}

export function CostPanel({ taskId }: { taskId: string }) {
  const cost = useTaskCost(taskId, true);
  if (cost.isPending) return <LoadingBlock />;
  if (cost.isError) {
    return (
      <ErrorBlock
        message={cost.error instanceof Error ? cost.error.message : 'Failed to load the cost'}
      />
    );
  }
  if (cost.data === null) return <EmptyBlock>This run recorded no usage.</EmptyBlock>;
  return <CostView cost={cost.data} />;
}

export function CostView({ cost }: { cost: TaskCost }) {
  // Group lines by event so the type chip and a reported line's units render once per event.
  const events = new Map<string, CostLine[]>();
  for (const line of cost.lines) {
    const group = events.get(line.usage_event_id) ?? [];
    group.push(line);
    events.set(line.usage_event_id, group);
  }
  const gaps = [
    cost.no_rate_types.length > 0 ? `no rate: ${cost.no_rate_types.join(', ')}` : null,
    cost.no_fx_types.length > 0 ? `no exchange rate: ${cost.no_fx_types.join(', ')}` : null,
  ].filter((g): g is string => g !== null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StatRow
        tiles={[
          { label: 'Cost', value: thb(cost.priced_total), icon: 'payments' },
          {
            label: 'Of which reported',
            value: `${thb(cost.reported_share)} · ${sharePercent(cost.reported_share, cost.priced_total)}`,
            icon: 'receipt',
          },
          {
            label: 'Organization pays',
            value: thb(cost.totals_by_payer.organization ?? '0'),
            icon: 'apartment',
          },
          { label: 'Platform pays', value: thb(cost.totals_by_payer.platform ?? '0'), icon: 'dns' },
        ]}
      />
      {gaps.length > 0 && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          Not in the total, {gaps.join('; ')}. A missing figure looks exactly like something cheap,
          so it is listed rather than hidden.
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ExportMenu
          storageKey="task"
          run={(columns) => exportTaskCostEvents(cost.subject_id, columns)}
        />
      </div>
      {cost.lines.length === 0 ? (
        <EmptyBlock>
          {cost.unpriced_events} event{cost.unpriced_events === 1 ? '' : 's'} recorded, not yet
          priced.
        </EmptyBlock>
      ) : (
        <DataTable
          rows={[...events.entries()].flatMap(([eventId, lines]) =>
            lines.map((line, i) => ({
              key: `${eventId}-${line.unit ?? line.status}`,
              line,
              first: i === 0,
            })),
          )}
          rowKey="key"
          rowStyle={({ line }) =>
            line.status === 'reported' ? { background: 'var(--info-tint)' } : undefined
          }
          columns={[
            {
              key: 'type',
              label: 'Type · resource',
              wrap: true,
              render: ({ line, first }) =>
                line.source === 'minimum' ? (
                  <Badge tone="neutral">minimum charge</Badge>
                ) : first ? (
                  <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
                    <Badge tone="info">{line.usage_type}</Badge>
                    <code
                      style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
                      className="mono"
                    >
                      {line.resource}
                    </code>
                    <Badge tone="neutral">{line.payer}</Badge>
                    {!line.declared && <Badge tone="danger">undeclared</Badge>}
                    {line.estimated && <Badge tone="warn">estimated</Badge>}
                  </span>
                ) : null,
            },
            {
              key: 'unit',
              label: 'Unit',
              mono: true,
              render: ({ line }) =>
                line.source === 'reported' ? (
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>
                      receipt {line.reported_amount} {line.reported_currency}
                    </span>
                    <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                      {unitsText(line.units)}
                    </span>
                  </span>
                ) : line.source === 'minimum' ? (
                  '—'
                ) : (
                  line.unit
                ),
            },
            {
              key: 'quantity',
              label: 'Quantity',
              align: 'right',
              mono: true,
              render: ({ line }) => (line.quantity === null ? '—' : quantity(line.quantity)),
            },
            {
              key: 'rate',
              label: 'Rate',
              align: 'right',
              mono: true,
              render: ({ line }) =>
                line.source === 'minimum' ? (
                  '—'
                ) : line.source === 'reported' ? (
                  line.fx_rate === null ? (
                    '—'
                  ) : (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 5,
                      }}
                    >
                      {line.fx_rate}
                      <span style={{ fontSize: 'var(--text-meta-lg)' }}>
                        as of {absoluteDate(line.fx_as_of ?? '')}
                      </span>
                      {line.fx_fallback && <Badge tone="warn">prior day</Badge>}
                    </span>
                  )
                ) : line.fx_rate === null ? (
                  perMillion(line.price_per_unit)
                ) : (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 5,
                    }}
                  >
                    {perMillion(line.price_per_unit)} · {line.fx_rate}
                    <span style={{ fontSize: 'var(--text-meta-lg)' }}>
                      as of {absoluteDate(line.fx_as_of ?? '')}
                    </span>
                    {line.fx_fallback && <Badge tone="warn">prior day</Badge>}
                  </span>
                ),
            },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              mono: true,
              render: ({ line }) =>
                line.amount !== null && (line.status === 'priced' || line.status === 'reported')
                  ? thbExact(line.amount)
                  : (line.reason ?? '—'),
            },
            {
              key: 'status',
              label: 'Status',
              render: ({ line }) => (
                <Badge tone={STATUS_LABEL[line.status].color}>
                  {STATUS_LABEL[line.status].label}
                </Badge>
              ),
            },
          ]}
        />
      )}
      <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        A reported line is the vendor's receipt, converted at the exchange rate of the run's day and
        frozen there; its units are shown for information. A rated line is one unit at the
        platform's rate card, which prices what the vendor did not report. A minimum charge line
        lifts an event whose costed lines sum below ฿0.01 to that floor; the lines above it stay
        exact.
      </p>
    </div>
  );
}
